/**
 * The post-login deep-link stash — shared by the auth gate and the API client.
 *
 * MSAL's `redirectUri` is pinned to `window.location.origin` because Entra requires every redirect
 * URI to sit in the app registration's allowlist and query strings aren't allowed there. So an OAuth
 * round-trip always returns the browser to `/`, and any deep-link path + query is lost unless we
 * remember it ourselves. Originally spec'd in
 * `docs/archive/pre-cut-2026-08/deep-link-routing-fix-spec.md` §5.
 *
 * ⚠ **Every interactive redirect must stash first, not just the sign-in button.** The gate stashed;
 * `api/client.ts`'s nine re-auth redirects did not — so a session that expired while the user sat on
 * a deep link dumped them back on the dashboard. That path is much more reachable now that the token
 * cache lives in localStorage ([msalConfig.ts](./msalConfig.ts)): a stale account can persist for
 * days, which makes "the gate renders, then the first API call 401s" the *ordinary* expiry
 * experience rather than a rarity.
 *
 * sessionStorage is deliberately the scope, even though the token cache is not: it is per-tab (two
 * tabs mid-login can't fight over one target), cleared on tab close, and never written to disk.
 */
export const DEEP_LINK_STASH_KEY = 'ember.pipeline.postLoginRedirect';

/** Remember where the user is, so the post-login mount can put them back. A no-op at `/`. */
export function stashDeepLink(): void {
  const target =
    window.location.pathname + window.location.search + window.location.hash;
  if (!target || target === '/') return;
  try {
    sessionStorage.setItem(DEEP_LINK_STASH_KEY, target);
  } catch {
    // sessionStorage can throw when disabled (private browsing on some platforms). Sign-in still
    // works; the deep link is simply lost, which is the pre-stash behaviour.
  }
}

/**
 * The stashed target, **consumed** — a second call returns null.
 *
 * Read-and-clear in one step deliberately: a target that outlives its restore would send the user
 * back to a record they had already navigated away from on the *next* re-auth.
 */
export function takeStashedDeepLink(): string | null {
  try {
    const stashed = sessionStorage.getItem(DEEP_LINK_STASH_KEY);
    if (stashed) sessionStorage.removeItem(DEEP_LINK_STASH_KEY);
    return stashed;
  } catch {
    return null;
  }
}
